import { useTranslation } from '@/composables';
import type { JsonValidationResultType, JsonParseResultType } from '@/types/composables/JsonUtilsTypes';

export const useJson = () => {
    const { t } = useTranslation();

    const parseJsonSafely = (jsonString: string): JsonParseResultType => {
        if (!jsonString || jsonString.trim() === '') {
            return {
                success: false,
                error: 'Empty JSON string',
            };
        }

        try {
            const data = JSON.parse(jsonString);

            return {
                success: true,
                data,
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Invalid JSON',
            };
        }
    };

    const validateJson = (jsonString: string): JsonValidationResultType => {
        if (!jsonString || jsonString.trim() === '') {
            return {
                isValid: false,
                error: t('validation.required'),
            };
        }

        const parseResult = parseJsonSafely(jsonString);

        if (!parseResult.success) {
            return {
                isValid: false,
                error: t('validation.invalidJson'),
            };
        }

        return {
            isValid: true,
            data: parseResult.data,
        };
    };

    const jsonValidator = (value: string): string | boolean => {
        const validation = validateJson(value);

        return validation.isValid ? true : validation.error || t('validation.invalidJson');
    };

    const getFirstJsonProperty = (input: string | any): string | null => {
        let data: any;

        if (typeof input === 'string') {
            const parseResult = parseJsonSafely(input);
            if (!parseResult.success || !parseResult.data) {
                return null;
            }
            data = parseResult.data;
        } else {
            data = input;
        }

        if (!data) {
            return null;
        }

        if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
            const firstKey = Object.keys(data)[0];
            if (firstKey) {
                const value = data[firstKey];

                return typeof value === 'string' ? value : JSON.stringify(value);
            }
        }

        if (Array.isArray(data) && data.length > 0) {
            const firstItem = data[0];

            return typeof firstItem === 'string' ? firstItem : JSON.stringify(firstItem);
        }

        return typeof data === 'string' ? data : JSON.stringify(data);
    };

    const formatJsonForDisplay = (data: any): string => {
        try {
            return JSON.stringify(data, null, 2);
        } catch {
            return 'Invalid JSON';
        }
    };

    const getJsonPropertyCount = (jsonString: string): number => {
        const parseResult = parseJsonSafely(jsonString);

        if (!parseResult.success || !parseResult.data) {
            return 0;
        }

        const data = parseResult.data;

        if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
            return Object.keys(data).length;
        }

        if (Array.isArray(data)) {
            return data.length;
        }

        return 1;
    };

    return {
        parseJsonSafely,
        validateJson,
        jsonValidator,
        getFirstJsonProperty,
        formatJsonForDisplay,
        getJsonPropertyCount,
    };
};
